import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import CustomCheckBox from 'components/Shared/CustomCheckBox'
import CustomRadioButton from 'components/Shared/CustomRadioButton'
import { useOutsideCheckClick } from 'hooks/UseOnClickOutside'
import { LIBRARY_RATIO_OPTIONS, LIBRARY_SORT_OPTIONS } from '../constant'

const FilterAccordion = ({ title, isOpen, onToggle, children }) => (
  <div className="library-filter__accordion">
    <button
      type="button"
      className={classnames('library-filter__accordion-header', { 'is-open': isOpen })}
      onClick={onToggle}
    >
      <span>{title}</span>
      <i className={`icon fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} />
    </button>

    {isOpen && <div className="library-filter__accordion-body">{children}</div>}
  </div>
)

FilterAccordion.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

const FilterDropdown = ({ sort, ratios, onSortChange, onRatioChange, onReset }) => {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [accordionOpen, setAccordionOpen] = useState({ sort: true, ratio: true })

  const hasFilters = Boolean(sort) || ratios.length > 0

  useOutsideCheckClick(containerRef, () => setIsOpen(false), isOpen)

  const handleToggleDropdown = () => {
    setIsOpen((prev) => {
      if (!prev) {
        setAccordionOpen({ sort: true, ratio: true })
      }
      return !prev
    })
  }

  const toggleAccordion = (key) => {
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div ref={containerRef} className="library-filter">
      <button
        type="button"
        onClick={handleToggleDropdown}
        className="library-filter__trigger"
      >
        <span className="library-filter__trigger-label">{t('Library.filter.title')}</span>
        <i className={`icon fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} />
      </button>

      {isOpen && (
        <div className="library-filter__dropdown">
          <FilterAccordion
            title={t('Library.filter.sort.title')}
            isOpen={accordionOpen.sort}
            onToggle={() => toggleAccordion('sort')}
          >
            <div className="library-filter__options">
              {LIBRARY_SORT_OPTIONS.map((optionKey) => (
                <CustomRadioButton
                  key={optionKey}
                  checked={sort === optionKey}
                  id={`library-sort-${optionKey}`}
                  name="library-sort"
                  label={t(`Library.filter.sort.options.${optionKey}`)}
                  onChange={() => onSortChange(optionKey)}
                />
              ))}
            </div>
          </FilterAccordion>

          <FilterAccordion
            title={t('Library.filter.ratio.title')}
            isOpen={accordionOpen.ratio}
            onToggle={() => toggleAccordion('ratio')}
          >
            <div className="library-filter__options">
              {LIBRARY_RATIO_OPTIONS.map((optionKey) => (
                <CustomCheckBox
                  key={optionKey}
                  checked={ratios.includes(optionKey)}
                  label={optionKey}
                  onChange={() => onRatioChange(optionKey)}
                />
              ))}
            </div>
          </FilterAccordion>

          {hasFilters && (
            <div className="library-filter__footer">
              <button type="button" className="library-filter__reset" onClick={onReset}>
                {t('Library.filter.resetAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

FilterDropdown.propTypes = {
  sort: PropTypes.string,
  ratios: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSortChange: PropTypes.func.isRequired,
  onRatioChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
}

export default FilterDropdown
